import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';

/**
 * Conference filters.
 *
 * The previous version was three rows of chunky bordered pill buttons — subject,
 * location, status — then two date fields and a sort dropdown, all inside a
 * 240px column. Four things were wrong with it, and they are why filtering here
 * felt like guesswork:
 *
 *   1. **No counts.** Every option looked equally promising. You clicked
 *      "Medicine", got nothing, and had to click it again to undo. The single
 *      biggest practicality win here is telling you what a filter will do
 *      *before* you commit to it.
 *   2. **Selected state was a solid primary fill**, so a set of active filters
 *      read as a row of buttons demanding to be pressed rather than as state.
 *      Checkboxes say "several of these can be on at once"; buttons don't.
 *   3. **Sort appeared twice** — once here and once passed to `SearchBar`, which
 *      silently ignored the props. So the visible sort control was this one and
 *      the other did nothing. Sort now lives in exactly one place, the toolbar,
 *      because it is not a filter: it never changes which conferences you see.
 *   4. Every option wrapped onto its own line at 240px anyway, so the pills were
 *      paying for horizontal flow they never got.
 *
 * Facet counts are computed against the *other* active filters, not against the
 * current result set — the standard behaviour, and the only one that lets you
 * see what widening a choice would add. `facetCounts` comes from the page,
 * which owns the data.
 *
 * A zero-count option is disabled rather than hidden, at the §5 disabled
 * treatment (opacity .45, `not-allowed`) — hiding options makes the filter list
 * jump around as you type, which is worse than a greyed row.
 */

/** One counted, toggleable facet row. */
function Facet({ id, label, icon, count, checked, disabled, onToggle }) {
  const { t } = useTranslation('conferences');
  return (
    <label
      className={`group flex items-center gap-2.5 h-8 px-2 -mx-2 cursor-pointer transition-colors ${
        disabled ? 'opacity-45 cursor-not-allowed' : 'hover:bg-muted'
      }`}
      title={disabled ? t('publicBoard.facetEmpty', 'No conferences match this right now') : undefined}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => onToggle(id)}
        /* Square, per the letterpress direction — and a checkbox rather than a
           radio because these genuinely combine. */
        className="w-3.5 h-3.5 flex-shrink-0 accent-primary border-input cursor-pointer disabled:cursor-not-allowed focus-ring"
      />
      {icon && (
        <Icon
          name={icon}
          size={14}
          className={`flex-shrink-0 ${checked ? 'text-primary' : 'text-muted-foreground'}`}
        />
      )}
      <span className={`flex-1 min-w-0 truncate text-[13px] ${checked ? 'text-foreground font-medium' : 'text-foreground'}`}>
        {label}
      </span>
      {/* Mono, tabular, end-aligned: counts are numerics, and they line up into
          a column you can read down. */}
      <span className="font-mono text-[11px] tabular-nums text-muted-foreground" dir="ltr">
        {count}
      </span>
    </label>
  );
}

/** A labelled group of facets. */
function Group({ label, children }) {
  return (
    <div>
      <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5">
        {label}
      </h3>
      <div className="space-y-px">{children}</div>
    </div>
  );
}

const FilterPanel = ({
  selectedSubjects,
  onSubjectToggle,
  selectedLocations,
  onLocationToggle,
  selectedStatuses,
  onStatusToggle,
  dateRange,
  onDateRangeChange,
  onClearFilters,
  facetCounts = { subjects: {}, locations: {}, statuses: {} },
}) => {
  const { t } = useTranslation('conferences');

  const subjectAreas = [
    { id: 'medicine', label: t('publicBoard.subjects.medicine'), icon: 'Stethoscope' },
    { id: 'ai', label: t('publicBoard.subjects.ai'), icon: 'Brain' },
    { id: 'business', label: t('publicBoard.subjects.business'), icon: 'Briefcase' },
    { id: 'engineering', label: t('publicBoard.subjects.engineering'), icon: 'Cog' },
    { id: 'science', label: t('publicBoard.subjects.science'), icon: 'Microscope' },
  ];

  const locationTypes = [
    { id: 'virtual', label: t('publicBoard.locations.virtual'), icon: 'Monitor' },
    { id: 'hybrid', label: t('publicBoard.locations.hybrid'), icon: 'Globe' },
    { id: 'in-person', label: t('publicBoard.locations.inPerson'), icon: 'MapPin' },
  ];

  const submissionStatuses = [
    { id: 'open', label: t('publicBoard.statuses.open') },
    { id: 'closing-soon', label: t('publicBoard.statuses.closingSoon') },
    { id: 'closed', label: t('publicBoard.statuses.closed') },
  ];

  const hasActiveFilters =
    selectedSubjects?.length > 0 ||
    selectedLocations?.length > 0 ||
    selectedStatuses?.length > 0 ||
    Boolean(dateRange?.start) ||
    Boolean(dateRange?.end);

  return (
    <div className="bg-card border border-border">
      <div className="flex items-center justify-between gap-2 h-11 px-4 border-b border-border">
        <h2 className="text-[13px] font-semibold text-foreground">
          {t('publicBoard.filters')}
        </h2>
        {/* Only offered once there is something to clear — a permanently
            visible "Clear all" trains you to ignore it. */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors focus-ring"
          >
            {t('publicBoard.clearAll')}
          </button>
        )}
      </div>

      <div className="p-4 space-y-5">
        <Group label={t('publicBoard.subjectArea')}>
          {subjectAreas?.map((s) => (
            <Facet
              key={s?.id}
              {...s}
              count={facetCounts?.subjects?.[s?.id] ?? 0}
              checked={selectedSubjects?.includes(s?.id)}
              disabled={!selectedSubjects?.includes(s?.id) && (facetCounts?.subjects?.[s?.id] ?? 0) === 0}
              onToggle={onSubjectToggle}
            />
          ))}
        </Group>

        <Group label={t('publicBoard.locationType')}>
          {locationTypes?.map((l) => (
            <Facet
              key={l?.id}
              {...l}
              count={facetCounts?.locations?.[l?.id] ?? 0}
              checked={selectedLocations?.includes(l?.id)}
              disabled={!selectedLocations?.includes(l?.id) && (facetCounts?.locations?.[l?.id] ?? 0) === 0}
              onToggle={onLocationToggle}
            />
          ))}
        </Group>

        <Group label={t('publicBoard.submissionStatus')}>
          {submissionStatuses?.map((st) => (
            <Facet
              key={st?.id}
              {...st}
              count={facetCounts?.statuses?.[st?.id] ?? 0}
              checked={selectedStatuses?.includes(st?.id)}
              disabled={!selectedStatuses?.includes(st?.id) && (facetCounts?.statuses?.[st?.id] ?? 0) === 0}
              onToggle={onStatusToggle}
            />
          ))}
        </Group>

        <Group label={t('publicBoard.dateRange', 'Starting between')}>
          <div className="space-y-2 pt-0.5">
            <Input
              type="date"
              label={t('publicBoard.startDate')}
              value={dateRange?.start}
              onChange={(e) => onDateRangeChange('start', e?.target?.value)}
            />
            <Input
              type="date"
              label={t('publicBoard.endDate')}
              value={dateRange?.end}
              onChange={(e) => onDateRangeChange('end', e?.target?.value)}
            />
          </div>
        </Group>
      </div>
    </div>
  );
};

export default FilterPanel;
